import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  takeUntil,
} from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { LoginAction, SetAuthLoading } from '../store/auth.actions';
import { AppError, ErrorCode } from 'src/app/core/models/api-error';
import { MfaChallenge } from '../models/mfa-challenge';

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss']
})
export class LoginPageComponent implements OnInit, OnDestroy {
  loginForm! : FormGroup;
  passwordVisible: boolean = false; // Initial state: password hidden
  /** The last login failure, kept on the page. The interceptor also toasts it,
   *  but that fades while the user is still retyping. */
  error$: Observable<AppError | null> = this.store.select((state) => state.auth.error);

  /** Non-null swaps the page to <app-mfa-challenge> in place. */
  challenge$: Observable<MfaChallenge | null> = this.store.select(
    (state) => state.auth.challenge
  );

  /** The last submit was refused outright, not challenged: only an
   *  administrator can clear it, so no retry is offered for this account.
   *  Editing the form clears it -- a different account is worth a try. */
  enrolmentRequired = false;

  private readonly destroy$ = new Subject<void>();

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }
  constructor(
    public fb: FormBuilder,
    private readonly store: Store<AppState>,
  ) { }

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      email: ['', Validators.compose([Validators.email, Validators.required])],
      password: ['', Validators.compose([Validators.required, Validators.minLength(4)])]
    });

    this.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        if (error?.code === ErrorCode.MFA_ENROLLMENT_REQUIRED) {
          this.enrolmentRequired = true;
        }
      });

    // Scoped to the account that was refused, not to the form: a different
    // email is a different account and must be submittable.
    this.loginForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.enrolmentRequired = false));

    // "Use another account" means abandoning it. Only the *ngIf content is
    // destroyed, so the form otherwise keeps the previous email and password.
    this.challenge$
      .pipe(
        map((challenge) => !!challenge),
        distinctUntilChanged(),
        pairwise(),
        filter(([had, has]) => had && !has),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loginForm.patchValue(
          { email: '', password: '' },
          { emitEvent: false }
        );
        this.loginForm.markAsUntouched();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loginAction() : boolean{
    // Also reached by Enter, which no hidden button can stop.
    if (this.enrolmentRequired) {
      return false;
    }
    localStorage.removeItem('currentUser');
    if(!this.loginForm.valid){
      this.loginForm.markAllAsTouched()
    return false;
    }
    this.store.dispatch(SetAuthLoading({isLoading: true}));
    this.store.dispatch(LoginAction({ payload: this.loginForm.value }));

    return true;
  }

  /**
   * `ui-form-field` calls `preventDefault()` on Enter, which also suppresses the
   * browser's implicit form submission — so submit explicitly. Scoped to inputs:
   * a button already turns Enter into a click, and letting this run there would
   * dispatch the login twice.
   */
  onEnterKey(event: Event): void {
    if ((event.target as HTMLElement | null)?.tagName === 'INPUT') {
      this.loginAction();
    }
  }

}
